import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  CircleAlert,
  CircleSlash,
  ClipboardList,
  ShieldAlert,
} from "lucide-react";

import {
  fetchGovernanceExceptions,
  GovernanceExceptionItem,
  GovernanceExceptionSeverity,
} from "@/api/governanceApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const RESERVED_SEGMENTS = new Set([
  "ddo",
  "pm",
  "pc",
  "finance",
  "management",
  "login",
  "403",
  "admin",
  "client",
]);

const severityOrder: GovernanceExceptionSeverity[] = [
  "blocked",
  "anomaly",
  "risk",
  "attention",
  "informational",
];

const severityCopy: Record<
  GovernanceExceptionSeverity,
  { label: string; className: string }
> = {
  blocked: {
    label: "Blocked",
    className: "border-red-200 bg-red-50 text-red-800",
  },
  anomaly: {
    label: "Anomaly",
    className: "border-violet-200 bg-violet-50 text-violet-800",
  },
  risk: {
    label: "Risk",
    className: "border-amber-200 bg-amber-50 text-amber-900",
  },
  attention: {
    label: "Attention",
    className: "border-blue-200 bg-blue-50 text-blue-800",
  },
  informational: {
    label: "Info",
    className: "border-slate-200 bg-slate-50 text-slate-700",
  },
};

interface GovernanceExceptionQueueProps {
  title: string;
  roleScope: "ddo" | "pc" | "pm" | "management" | "finance" | "client";
  submissionPathPrefix?: string;
  submissionHrefBuilder?: (tenantSlug: string, submissionId: number) => string;
  openSubmissionLabel?: string;
  emptyCopy?: string;
  subtitle?: string;
  taskOrientedEmptyState?: boolean;
  alignStart?: boolean;
}

function resolveTenantSlug(paramSlug: string | undefined): string {
  const cleanParam = paramSlug?.trim();
  if (cleanParam && !RESERVED_SEGMENTS.has(cleanParam)) {
    return cleanParam;
  }

  const stored = sessionStorage.getItem("tenant_slug")?.trim();
  if (stored && !RESERVED_SEGMENTS.has(stored)) {
    return stored;
  }

  return "default";
}

function relativeTime(value: string): string {
  try {
    return `${formatDistanceToNowStrict(parseISO(value))} ago`;
  } catch {
    return "recently";
  }
}

function severityIcon(severity: GovernanceExceptionSeverity) {
  const className = "h-4 w-4 shrink-0";
  if (severity === "blocked") return <CircleSlash className={className} />;
  if (severity === "anomaly") return <ShieldAlert className={className} />;
  if (severity === "risk") return <AlertTriangle className={className} />;
  if (severity === "attention") return <CircleAlert className={className} />;
  return <ClipboardList className={className} />;
}

const GovernanceExceptionQueue: React.FC<GovernanceExceptionQueueProps> = ({
  title,
  roleScope,
  submissionPathPrefix,
  submissionHrefBuilder,
  openSubmissionLabel = "Open",
  emptyCopy = "No active governance exceptions.",
  subtitle = "Review blocked, anomalous, and aging submissions.",
  taskOrientedEmptyState = false,
  alignStart = false,
}) => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const [items, setItems] = useState<GovernanceExceptionItem[]>([]);
  const [bySeverity, setBySeverity] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const slug = resolveTenantSlug(tenantSlug);

  const loadExceptions = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchGovernanceExceptions({
        limit: 100,
        role_scope: roleScope,
      });
      setItems(res.items);
      setBySeverity(res.by_severity);
    } catch (err) {
      console.error(err);
      setError(
        taskOrientedEmptyState
          ? "Failed to load applications that need attention."
          : "Failed to load governance exceptions."
      );
    } finally {
      setLoading(false);
    }
  }, [roleScope, taskOrientedEmptyState]);

  useEffect(() => {
    void loadExceptions();
  }, [loadExceptions]);

  const groupedItems = useMemo(() => {
    return severityOrder
      .map((severity) => ({
        severity,
        items: items.filter((item) => item.severity === severity),
      }))
      .filter((group) => group.items.length > 0);
  }, [items]);

  const openSubmission = (submissionId: number) => {
    if (submissionHrefBuilder) {
      navigate(submissionHrefBuilder(slug, submissionId));
      return;
    }
    if (!submissionPathPrefix) return;
    navigate(`/${slug}${submissionPathPrefix}/${submissionId}`);
  };

  return (
    <div
      className={`max-w-[1400px] space-y-6 p-4 sm:p-6 ${
        alignStart ? "w-full" : "mx-auto"
      }`}
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-sm text-muted-foreground">
            {subtitle}
          </p>
        </div>
        <Button variant="outline" onClick={() => void loadExceptions()}>
          Refresh
        </Button>
      </div>

      {taskOrientedEmptyState && !loading && !error && items.length === 0 ? null : (
        <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
          {severityOrder.map((severity) => {
            const meta = severityCopy[severity];
            return (
              <div
                key={severity}
                className={`rounded-md border px-3 py-2 ${meta.className}`}
              >
                <div className="text-xs font-medium">{meta.label}</div>
                <div className="text-2xl font-semibold">
                  {bySeverity[severity] ?? 0}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {taskOrientedEmptyState ? "Applications requiring action" : "Active governance signals"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, idx) => (
                <Skeleton key={idx} className="h-20 w-full" />
              ))}
            </div>
          ) : null}

          {!loading && error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              {emptyCopy}
            </div>
          ) : null}

          {!loading && !error && groupedItems.length > 0 ? (
            <div className="space-y-5">
              {groupedItems.map((group) => (
                <section key={group.severity} className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {severityIcon(group.severity)}
                    {severityCopy[group.severity].label}
                  </div>

                  <div className="space-y-2">
                    {group.items.map((item) => {
                      const submissionId = item.submission_id;
                      return (
                        <div
                          key={item.exception_id}
                          className="rounded-md border bg-background p-4"
                        >
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                {submissionId ? (
                                  <Badge variant="outline">
                                    Submission #{submissionId}
                                  </Badge>
                                ) : null}
                                {item.status ? (
                                  <Badge variant="secondary">{item.status}</Badge>
                                ) : null}
                                {item.age_days != null ? (
                                  <Badge variant="outline">
                                    {item.age_days} days
                                  </Badge>
                                ) : null}
                                {item.metric_value != null ? (
                                  <Badge variant="outline">
                                    Metric {item.metric_value}
                                  </Badge>
                                ) : null}
                              </div>

                              <div>
                                <div className="font-medium">{item.title}</div>
                                <p className="text-sm text-muted-foreground">
                                  {item.detail}
                                </p>
                              </div>

                              {item.recommended_action ? (
                                <p className="text-sm text-slate-700">
                                  {item.recommended_action}
                                </p>
                              ) : null}
                            </div>

                            <div className="flex shrink-0 flex-col gap-2 text-left lg:items-end">
                              <div className="text-xs text-muted-foreground">
                                {relativeTime(item.detected_at)}
                              </div>
                              {(submissionPathPrefix || submissionHrefBuilder) && submissionId ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openSubmission(submissionId)}
                                >
                                  {openSubmissionLabel}
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default GovernanceExceptionQueue;
